import { call, put, select } from 'typed-redux-saga/macro';
import { readBurnInLabel } from '@helpers/redactedFileStaleness';
import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { selectPendingDocument } from '../selectors';
import { markDocumentOpen } from '@redact-components/DocumentEditorPage/mainThreadSpans';

import {
  OPEN_DOCUMENT,
  OPEN_DOCUMENT_FAILURE,
  HYDRATE_WATERMARK_IMAGES,
  OPEN_DOCUMENT_SUCCESS,
} from '../actions';
import {
  isUploadWrittenRedactionAsset,
  serviceFetchTdoMediaInfo,
  serviceListAuditLogAssets,
  serviceListRedactedDocumentAssets,
  serviceLoadDocumentRedactionData,
  serviceLoadWatermarkImageAsset,
} from '../services';
import {
  appliedLexiconsFromAssetBody,
  excludedPagesFromAssetBody,
  lexiconDetectionsFromAssetBody,
  pageRedactsFromAssetBody,
  piiMatchesFromAssetBody,
  readAssetDownloadCount,
  readAssetManualRedactionCount,
  readAssetNumPages,
  readAssetPagesRemovedCount,
  readAssetPagesFullyRedactedCount,
  readAssetKeywordMetrics,
  readAssetPiiMetrics,
  readAssetTextSelectMetrics,
  readAssetVersion,
  redactionsFromAssetBody,
  stripLegacyGlyphPad,
  batesStampFromAssetBody,
  batesStampOffFromAssetBody,
  watermarksFromAssetBody,
  watermarksOffFromAssetBody,
} from '../helpers';
import { deriveWatermarkImage } from '../watermarkImage';
import {
  PersistedRedactionState,
  RedactedVersion,
  VendorRequestIds,
} from '../models';
import {
  isAssetFormatSupported,
  MIN_SUPPORTED_ASSET_FORMAT_VERSION,
} from '../store';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import { actionMarkTdoReadVerified } from '@common-modules/statusHistory/actions';
import { getRedactionStatusIdentity } from '@common-modules/mediaDetails/redactionStatuses';
import { selectToken, selectGraphQLConfig } from './auth';

function* warnRedactionRestoreLoadFailed() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'documentRedactionRestoreLoadFailed',
        defaultMessage:
          "Couldn't restore your last saved redactions. You're starting with a blank document.",
      }),
      variant: 'warning',
    })
  );
}

function* warnRedactionRestoreUnsupportedFormat() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'documentRedactionRestoreUnsupportedFormat',
        defaultMessage:
          "Your saved redactions use an older format and couldn't be restored. You're starting with a blank document.",
      }),
      variant: 'warning',
    })
  );
}

export function* handleOpenDocument({
  payload: { tdoId, keywordSearchSeed, viewSeed },
}: ReturnType<typeof OPEN_DOCUMENT>) {
  try {
    const token: string = yield* select(selectToken);
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      yield* select(selectGraphQLConfig);

    // Pull the real PDF URL + name from the TDO's "media" asset. Best-effort:
    // if it throws (network / auth / etc.) the editor still opens with a
    // synthesized name and no PDF rather than blocking on a side concern.
    let mediaUri: string | null = null;
    let mediaAssetId: string | null = null;
    // Whether the asset above is OCR's own output — see `mediaAssetEngineId`.
    let mediaAssetEngineId: string | null = null;
    let tdoName: string | null = null;
    // Canonical filter value (e.g. 'draft'). Read from the TDO's tags so the
    // editor's status dropdown opens on the value the rest of the app uses
    // (VE-21914). Null → reducer falls back to the default in the UI.
    let redactionStatus: string | null = null;
    let redactionStatusName: string | null = null;
    // Vendor "Send to" request-ids + folder context (VE-23442). Default to the
    // empty shape so a media-info fetch failure just hides the send buttons.
    let vendorRequestIds: VendorRequestIds = {
      govQa: null,
      foiaXpress: null,
      casepoint: null,
      exterro: null,
      nuix: null,
    };
    let folderId: string | null = null;
    let folderName: string | null = null;
    try {
      const info = yield* call(
        serviceFetchTdoMediaInfo,
        tdoId,
        token,
        apiRoot,
        graphQLEndpoint,
        veritoneAppId
      );
      mediaUri = info.mediaUri;
      mediaAssetId = info.mediaAssetId;
      mediaAssetEngineId = info.mediaAssetEngineId;
      tdoName = info.name;
      vendorRequestIds = info.vendorRequestIds;
      folderId = info.folderId;
      folderName = info.folderName;
      const statusTag = info.tags.find(
        (t) => t?.value === IN_REDACTION_TAG_KEY && !!t.redactionStatus
      );
      if (statusTag) {
        // Identity = key when present (custom), else the stored name (built-in
        // label, or a legacy custom UUID). Keep the frozen name for orphan
        // display if the custom status was later deleted (VE-24546).
        redactionStatus =
          getRedactionStatusIdentity(statusTag) ?? redactionStatus;
        redactionStatusName = statusTag.redactionStatus ?? null;
      }
      // VE-26730 / SECURITY-11: the media-info fetch above is the ONLY
      // server-acknowledged read of this TDO on the document route — it throws
      // on any GraphQL error, including an authorization denial, so reaching
      // this line proves the caller may read this file. Mark it verified for
      // the statusHistory read funnel HERE, inside the try. The
      // OPEN_DOCUMENT_SUCCESS dispatched at the end of this saga is NOT proof:
      // it fires with the client-echoed tdoId even when this fetch failed (the
      // catch below deliberately degrades instead of aborting the open).
      yield* put(actionMarkTdoReadVerified({ tdoId: String(tdoId) }));
    } catch (e) {
      console.warn(
        'Document open: TDO media-info GraphQL fetch failed; opening with a synthesized name and no PDF.',
        e
      );
    }

    // Load the most-recent `document-redaction-data` asset, if any. This is
    // VE-20835's real-persistence path and runs against GraphQL directly.
    // Failures are non-fatal: the editor still opens (with no restored state).
    let persistedRedactions: PersistedRedactionState | null = null;
    // `createdDateTime` of the most-recent document-redaction-data asset.
    // Drives the "Last saved" indicator in the editor footer so reopening a
    // document shows the prior save time without waiting for the next save
    // to fire SAVE_SUCCESS.
    let lastSavedAt: string | null = null;
    /**
     * VE-26422: which output version the newest save was sent to produce, or
     * null when it was an ordinary save that was never sent. Undefined stays
     * undefined — an asset written before labelling says nothing either way, and
     * the verdict must not read that as "never sent".
     */
    let lastSavedBurnInVersion: number | null | undefined;
    try {
      const loaded = yield* call(
        serviceLoadDocumentRedactionData,
        tdoId,
        token,
        apiRoot,
        graphQLEndpoint,
        veritoneAppId
      );
      // Version gate: load only asset formats >= the minimum. Anything older
      // (e.g. 0.0.3) is ignored entirely (no migration) — the editor opens with
      // empty redaction state.
      const loadedFormat = (
        loaded?.body as { assetFormatVersion?: string } | undefined
      )?.assetFormatVersion;
      if (loaded && !isAssetFormatSupported(loadedFormat)) {
        console.warn(
          `Document open: ignoring redaction asset with unsupported format ` +
            `"${loadedFormat ?? 'unknown'}" (loader requires >= ` +
            `${MIN_SUPPORTED_ASSET_FORMAT_VERSION}). ` +
            `Opening without prior redactions.`
        );
        yield* warnRedactionRestoreUnsupportedFormat();
      } else if (loaded) {
        // DOC-TODO: detectionRectOverrides persistence is out of scope for
        // VE-20835. pageRedacts + excludedPages are persisted via
        // `pageContentRemoved` / `pageHidden` bucket flags. PII selections
        // (VE-21915) are persisted as per-match records under each page
        // bucket's `PII` array.
        persistedRedactions = {
          schemaVersion: 1,
          // Legacy 0.0.3 glyph-anchored boxes baked in a +2 pad — normalize to
          // the neutral box on load (see stripLegacyGlyphPad). No-op for 1.0.0+.
          redactions: stripLegacyGlyphPad(
            redactionsFromAssetBody(loaded.body),
            loadedFormat
          ),
          detectionRectOverrides: {},
          pageRedacts: pageRedactsFromAssetBody(loaded.body),
          excludedPages: excludedPagesFromAssetBody(loaded.body),
          detectionState: [],
          piiMatches: piiMatchesFromAssetBody(loaded.body),
          // Lexicon detections (VE-24948 / U5) are rebuilt WHOLE from the asset
          // — snapshot, geometry and triage state — without consulting any
          // lexicon. That is what makes `U5-FR2` hold: a lexicon edited or
          // deleted after application cannot reach back into a saved document.
          lexiconDetections: lexiconDetectionsFromAssetBody(loaded.body),
          appliedLexicons: appliedLexiconsFromAssetBody(loaded.body),
          // VE-24693: restore applied watermarks (snapshots) on reopen (AC#3).
          watermarks: watermarksFromAssetBody(loaded.body),
          watermarksOff: watermarksOffFromAssetBody(loaded.body),
          // VE-24447: restore Bates settings on reopen, so the next export
          // renumbers identically rather than silently dropping the stamp.
          batesStamp: batesStampFromAssetBody(loaded.body) ?? undefined,
          batesStampOff: batesStampOffFromAssetBody(loaded.body) ?? undefined,
        };
        // VE-24948 (review B4): an asset the UPLOAD pipeline stamped is not a
        // save, and must not be reported as one. Its contents are still loaded
        // above — the lexicons picked at upload are exactly what this open has
        // to evaluate (BR-50) — but `lastSavedAt` stays null, because both
        // auto-apply sagas read it as "the reviewer has saved this document,
        // so its own contents are the answer". They cannot be told that about
        // a document nobody has opened yet.
        if (!isUploadWrittenRedactionAsset(loaded.details)) {
          lastSavedAt = loaded.createdDateTime;
          const label = readBurnInLabel(loaded.details);
          lastSavedBurnInVersion =
            label.state === 'burnedIn'
              ? label.version
              : label.state === 'saved'
                ? null
                : undefined;
        }
      }
    } catch (e) {
      console.warn(
        'Document open: redaction-data load failed; opening without prior redactions.',
        e
      );
      yield* warnRedactionRestoreLoadFailed();
    }

    // List existing `redacted-document` assets so the Redacted Files tab can
    // show prior burn-ins immediately on reopen. Today the burn-in saga
    // deletes all older assets after each save (per ticket), so this list is
    // 0 or 1 entries — but the mapping is shaped to handle any count if that
    // policy ever loosens. Failures are non-fatal: empty versionHistory just
    // means the tab will be blank until the next burn-in.
    let versionHistory: RedactedVersion[] = [];
    try {
      const redactedAssets = yield* call(
        serviceListRedactedDocumentAssets,
        tdoId,
        token,
        apiRoot,
        graphQLEndpoint,
        veritoneAppId
      );
      // VE-20840: list the audit-log CSV assets and index them by the
      // redacted-document asset they were written for, so each version can
      // re-attach its `auditLogUrl` on reopen. Best-effort — a failure here
      // leaves auditLogUrl empty (button hidden) without blocking the tab.
      const auditLogUrlByAssetId = new Map<string, string>();
      try {
        const auditAssets = yield* call(
          serviceListAuditLogAssets,
          tdoId,
          token,
          apiRoot,
          graphQLEndpoint,
          veritoneAppId
        );
        for (const a of auditAssets) {
          const linkedId = a.details?.redactedMediaAssetId;
          if (linkedId && a.signedUri && !auditLogUrlByAssetId.has(linkedId)) {
            auditLogUrlByAssetId.set(linkedId, a.signedUri);
          }
        }
      } catch (e) {
        console.warn(
          'Document open: audit-log asset list failed; download button hidden.',
          e
        );
      }
      // `serviceListRedactedDocumentAssets` returns DESC by createdDateTime
      // (newest first), but UI consumers treat `versionHistory[length-1]`
      // as the latest. Reverse so the ordering is ascending (oldest first /
      // newest last) and the "latest" idiom keeps working consistently
      // across both the saga's optimistic single-entry insert and a
      // reopen with multiple persisted assets.
      versionHistory = redactedAssets
        .map((a) => {
          const v = readAssetVersion(a.details);
          return {
            versionId: a.id,
            version: v > 0 ? v : null,
            redactedAt: a.createdDateTime,
            // Null when the asset has no signedUri yet (rare; transient
            // server state). Downstream consumers guard against null.
            redactedAssetUrl: a.signedUri ?? null,
            name: a.name,
            assetSize: a.assetSize,
            numPages: readAssetNumPages(a.details),
            pagesRemoved: readAssetPagesRemovedCount(a.details),
            pagesFullyRedacted: readAssetPagesFullyRedactedCount(a.details),
            // VE-20840: re-attach the matching audit-log CSV's signedUri (the
            // audit asset's details.redactedMediaAssetId points back at this
            // asset id). Empty when no audit log exists for this version.
            auditLogUrl: auditLogUrlByAssetId.get(a.id) ?? '',
            redactionCount: readAssetManualRedactionCount(a.details),
            textSelect: readAssetTextSelectMetrics(a.details),
            keywords: readAssetKeywordMetrics(a.details),
            pii: readAssetPiiMetrics(a.details),
            downloads: readAssetDownloadCount(a.details),
            pushedTo: [],
          };
        })
        .reverse();
    } catch (e) {
      console.warn(
        'Document open: redacted-document list failed; Redacted Files tab will be empty.',
        e
      );
    }

    // t0 for the open-to-first-page timeline. It has to be HERE, not at the
    // canvas's mount: the dims pre-pass saga also triggers on this action, so a
    // reset from the canvas (which mounts later) would clear its milestone.
    markDocumentOpen();
    yield* put(
      OPEN_DOCUMENT_SUCCESS({
        tdoId: tdoId,
        // Real TDO name from GraphQL; fall back to a synthesized label only if
        // the media-info fetch returned no name.
        documentName: tdoName ?? `Document ${tdoId}`,
        // Real signed PDF URL from GraphQL. Empty when unavailable — the canvas
        // renders its "no PDF" state rather than a sample.
        pdfUrl: mediaUri ?? '',
        mediaAssetId,
        mediaAssetEngineId,
        // Seed of 1; react-pdf reports the real page count on load and
        // overwrites this via setTotalPages.
        totalPages: 1,
        // Detections always start empty; the PII engine's result asset is
        // loaded separately by the engine-status poller / PII_RESULTS_LOADED.
        detections: [],
        savedState: persistedRedactions ?? null,
        versionHistory,
        lastSavedAt,
        lastSavedBurnInVersion,
        redactionStatus,
        redactionStatusName,
        piiMatches: persistedRedactions?.piiMatches ?? [],
        vendorRequestIds,
        folderId,
        folderName,
        keywordSearchSeed,
        viewSeed,
      })
    );

    // VE-24693: rebuild each image watermark's DRAWN form. The asset stores only
    // the colour base and the black-&-white / colour choices, because the drawn
    // form is a pure function of them — persisting it as well doubled the asset
    // and gave the export a copy that could fall out of step with the flags.
    //
    // After OPEN_DOCUMENT_SUCCESS, not before: the page renders immediately and
    // the marks appear a beat later, rather than the canvas waiting on a canvas
    // pass per mark. Best-effort — a mark that fails to derive simply has
    // nothing to draw, which is the same as its base being unreadable.
    const restored = persistedRedactions?.watermarks ?? [];
    // Every image mark that got this far, which since VE-28770 is every one
    // that has a raster or a pointer to one: `watermarksFromAssetBody` drops a
    // stored mark carrying neither, because that is not a watermark — nothing
    // can draw it and nothing can recover it, and listing it told the operator
    // a mark was applied to a document that exported unmarked.
    //
    // So a failure HERE always means artwork that should have been readable and
    // was not, which is what makes the warning below worth interrupting for.
    const toHydrate = restored.filter((wm) => wm.type === 'image');
    if (toHydrate.length > 0) {
      const images: Array<{
        instanceId: string;
        imageDataUrl: string;
        imageBaseDataUrl?: string;
      }> = [];
      // Ids, not just a count: the panel has to name the rows it marks, and a
      // number cannot (VE-28770). The count stays because the snackbar says it.
      const failedInstanceIds: string[] = [];
      let failed = 0;
      for (const wm of toHydrate) {
        try {
          // Format 1.3.0: the raster lives in its own asset, so fetch it first.
          // Once per mark, for the whole session — the base is then held in the
          // session and every later black-&-white or tint change re-derives
          // from memory, and burn-in reuses the drawn form built here rather
          // than fetching anything at export time.
          //
          // A 1.2.0 body still has its base inline and needs no fetch.
          const base = wm.imageBaseDataUrl
            ? wm.imageBaseDataUrl
            : wm.imageAssetId
              ? yield* call(
                  serviceLoadWatermarkImageAsset,
                  wm.imageAssetId,
                  tdoId,
                  token,
                  apiRoot,
                  graphQLEndpoint,
                  veritoneAppId
                )
              : // REACHED, and by design — do not delete it. A mark written
                // with nothing at all is dropped at `watermarksFromAssetBody`,
                // but a mark whose stored artwork this client REFUSED is
                // deliberately KEPT there and arrives here with both fields
                // nulled by the sanitizer: a non-PNG, an oversized PNG, an
                // `https://` source — any of which the public API can write.
                // That row is supposed to be counted and reported, and this
                // fallback is how it reaches `!base` to be.
                null;
          if (!base) {
            failedInstanceIds.push(wm.instanceId);
            // The asset is gone (retention, a failed write, a hand-edited
            // body). The mark KEEPS its `imageAssetId` — a network fault reads
            // exactly like a deleted asset from here, and clearing the id would
            // turn one bad request into permanent loss on the next save. The id
            // plus this instance being reported is how the panel tells "gone"
            // from "not here yet".
            // TODO(VE-24693): consider raising an error here instead — a
            // missing raster means the export silently ships without its
            // marking. See plans/VE-24693-watermark-data-model-revisions.md §J.
            failed += 1;
            continue;
          }
          const drawn = yield* call(deriveWatermarkImage, base, {
            grayscale: wm.grayscale,
            color: wm.color,
          });
          images.push({
            instanceId: wm.instanceId,
            imageDataUrl: drawn,
            imageBaseDataUrl: base,
          });
        } catch (err) {
          failedInstanceIds.push(wm.instanceId);
          failed += 1;
          console.warn('Watermark image could not be restored', err);
        }
      }
      // Dispatched for a run that restored NOTHING too, which it did not used
      // to be: an open where every mark failed is exactly the one the panel
      // most needs told about, and the old `images.length > 0` guard made it
      // the one open that said nothing.
      if (images.length > 0 || failedInstanceIds.length > 0) {
        yield* put(
          HYDRATE_WATERMARK_IMAGES({
            images,
            origin: 'restore',
            failedInstanceIds,
          })
        );
      }
      if (failed > 0) {
        // Said out loud, because the consequence is invisible and permanent:
        // `toBurnInWatermark` drops an image mark with no `imageDataUrl`, so a
        // failure here means the operator exports a document whose watermark is
        // simply not there. On a feature whose entire job is marking documents,
        // "it silently did not happen" is the one outcome nobody can afford.
        yield* put(
          enqueueSnackbar({
            message: sagaIntl().formatMessage(
              {
                id: 'watermarkImageRestoreFailed',
                defaultMessage:
                  '{count, plural, one {A watermark image} other {# watermark images}} could not be restored and will not appear in the exported file.',
              },
              { count: failed }
            ),
            variant: 'warning',
          })
        );
      }
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to open document';
    // A failed SWITCH abandons the switch and leaves the reviewer on the
    // document they were already editing (see the reducer), so nothing on
    // screen changes — which means nothing on screen would tell them the file
    // they picked never opened. Say it here; the error screen only covers the
    // initial-open case.
    const pending = yield* select(selectPendingDocument);
    if (pending) {
      yield* put(
        enqueueSnackbar({
          message: `Could not open "${pending.documentName}". Staying on the current document.`,
          variant: 'error',
        })
      );
    }
    yield* put(OPEN_DOCUMENT_FAILURE({ message }));
  }
}
